// Minnesotaänger Mini App — message list bottom reserve sync.
// Trimmed from telegram-tt's messageListReserves.ts: the send-collapse
// latch (composer collapse animation after sending), the sender-avatar
// offset and `isAnimatingScroll` (its util module is not part of v1) were
// removed. Kept: the footer-height measure and the bottom-inset sync that
// make `.messages-container` padding and the scroll-snap anchor track the
// absolute composer footer.

import { forceMutation, requestMeasure } from '../../../lib/fasterdom/fasterdom';
import buildStyle from '../../../util/buildStyle';

const MESSAGE_LIST_COMPOSER_GAP = 0.5 * 16; // Mirrors `--message-list-composer-gap` (0.5rem)
const NO_FOOTER_CLASS = 'no-footer';
const SELECT_MODE_CLASS = 'select-mode-active';
export const AT_BOTTOM_THRESHOLD = 4;
export const SCROLL_BOTTOM_SENTINEL = 1e7;

// Matches the desktop `--composer-min-bottom-reserve`; used only before stylesheets load
const FALLBACK_MIN_BOTTOM_INSET = 4 * 16;

export function getMessageListBottomReserve(scroller: HTMLElement) {
  if (scroller.classList.contains(SELECT_MODE_CLASS)) {
    return getSettledBottomReserve();
  }
  if (scroller.classList.contains(NO_FOOTER_CLASS)) return 0;
  const footer = scroller.parentElement?.querySelector<HTMLElement>(':scope > .middle-column-footer');
  if (!footer) return 0;
  if (getComputedStyle(footer).position !== 'absolute') return 0;
  return Math.max(measureFooterContentHeight(footer) + MESSAGE_LIST_COMPOSER_GAP, getMinBottomInset());
}

export function measureFooterContentHeight(footer: HTMLElement) {
  const actionBar = footer.querySelector<HTMLElement>('[data-footer-action-bar]');
  return Math.max(footer.offsetHeight, actionBar?.offsetHeight ?? 0);
}

function getSettledBottomReserve() {
  return getMinBottomInset() + MESSAGE_LIST_COMPOSER_GAP;
}

export function applyMessageListBottomInset(scroller: HTMLElement, bottomReserve: number) {
  const inset = bottomReserve > 0 ? `${bottomReserve}px` : '';
  const fade = bottomReserve > 0 ? `${Math.max(bottomReserve - MESSAGE_LIST_COMPOSER_GAP, 0)}px` : '';

  scroller.style.setProperty('--message-list-bottom-inset', inset);
  scroller.style.setProperty('--message-list-bottom-fade', fade);

  scroller.querySelectorAll<HTMLElement>('.messages-container').forEach((container) => {
    container.style.paddingBottom = inset;
  });
}

export function syncMessageListBottomReserve(
  scroller: HTMLElement, shouldSkipKeepAtBottom = false, forceKeepAtBottom = false,
) {
  const bottomReserve = getMessageListBottomReserve(scroller);
  const isAtBottom = forceKeepAtBottom
    || scroller.scrollHeight - scroller.scrollTop - scroller.offsetHeight <= AT_BOTTOM_THRESHOLD;
  const canKeepAtBottom = !shouldSkipKeepAtBottom;

  const insetTargets: HTMLElement[] = [
    // `applyMessageListBottomInset` writes the reserve/fade vars on the scroller itself, so it must be allowed too.
    scroller,
    ...scroller.querySelectorAll<HTMLElement>('.messages-container'),
  ];

  forceMutation(() => {
    applyMessageListBottomInset(scroller, bottomReserve);
    if (isAtBottom && canKeepAtBottom) {
      scroller.scrollTop = SCROLL_BOTTOM_SENTINEL;
    }
  }, insetTargets);
}

let minBottomInsetProbe: HTMLDivElement | undefined;

// The single source is `--composer-min-bottom-reserve`; its `calc()` cannot be parsed off
// a custom property, so a permanently mounted probe element evaluates it. The value is
// responsive (viewport, safe area, `body.keyboard-visible`), so it is re-read on every call.
function getMinBottomInset() {
  if (!minBottomInsetProbe) {
    minBottomInsetProbe = document.createElement('div');
    minBottomInsetProbe.style.cssText = buildStyle(
      'position: absolute',
      'visibility: hidden',
      'height: var(--composer-min-bottom-reserve)',
    );
    document.body.appendChild(minBottomInsetProbe);
  }

  return minBottomInsetProbe.offsetHeight || FALLBACK_MIN_BOTTOM_INSET;
}

// Kept exported for parity with telegram-tt; v1 renders no sender avatars.
export function getMessageListTopReserve(scroller: HTMLElement) {
  const middleColumn = scroller.closest<HTMLElement>('#MiddleColumn');
  if (!middleColumn) return 0;

  const scrollerTop = scroller.getBoundingClientRect().top;
  let bottom = 0;

  const header = middleColumn.querySelector<HTMLElement>('.MiddleHeader');
  if (header?.offsetParent) {
    bottom = header.getBoundingClientRect().bottom - scrollerTop;
  }

  return Math.max(0, bottom);
}
